import React, { useEffect } from 'react';

import config from 'config';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';

import { getExperienceId } from '~/acquisition/utils/customizedExperience';
import { isBuilderPreview } from '~/builder/utils/isBuilderPreview';
import {
  allComponents,
  registerBuilderComponents,
  registerBuilderMenu,
} from '~/builder/utils/registerBuilderComponents';
import DefaultLayout from '~/layout/components/DefaultLayout';
import PDPContent from '~/pdp/components/PDPContent';
import { useProductQueryAndPreload } from '~/pdp/utils/useProductQueryAndPreload';
import featureNames from '~/shared/constants/featureFlags';
import { productDetailPath } from '~/shared/constants/product';
import { SecondarySites } from '~/shared/constants/secondarySites';
import { getBrandValue } from '~/shared/utils/brandNameHelper';
import CuralateUgcSnippet from '~/shared/utils/CuralateUgcSnippet';
import transformIdentifier from '~/shared/utils/transformIdentifier';
import useGridGender from '~/shared/utils/useGridGender';
import useHasFeature from '~/shared/utils/useHasFeature';
import { getSecondarySite } from '~/shared/utils/useSecondarySite';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { StyleContext } from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';
import { getDirectMarketingDisposition } from '~/techstyle-shared/react-marketing';
import {
  useDomain,
  useSession,
  loadSession,
  getSession,
  loadSessionDetails,
} from '~/techstyle-shared/redux-core';

import createPage from '../../registry';

if (process.browser) {
  require('intersection-observer');
}

const defaults = defineMessages({
  metaDescription: {
    id: 'site_seo_description.meta_description_default',
    defaultMessage: 'Buy {productLabel} from Fabletics.', // this can contain offer information
  },
  metaTitleOfferDefault: {
    id: 'site_pdp.title_tag_offer_default',
    defaultMessage: '- {brand}',
  },
  productNotFoundModalTitle: {
    id: 'site_product_page.product_not_found_title',
    defaultMessage: "We're Sorry",
  },
});

function ProductDetailPage({ masterProductId, permalink, builderQueries }) {
  const { isLoggedIn } = useSession();
  const { isVip, membershipLevelGroup } = useMembership();
  const { tld } = useDomain();
  const isUSDomain = tld === '.com';
  const router = useRouter();
  const secondarySite = getSecondarySite(router.asPath);

  const { formatMessage } = useIntl();

  const loadWarehouseInventory = isLoggedIn && isVip && isUSDomain;

  const gridGender = useGridGender();

  if (secondarySite) {
    const basePath = `/${secondarySite}/${productDetailPath}/`;
    const identifierPattern = '([A-Z0-9\\-]+)';
    const permalinkRegex = new RegExp(`${basePath}${identifierPattern}`);
    const match = router.asPath.match(permalinkRegex);

    if (match) {
      permalink = match[1];
    }
  }

  const useRetailPriceForDiscount = useHasFeature(featureNames.RETAILPRICETEST);
  const { product, baseProductLoading, relatedProductsLoading, productError } =
    useProductQueryAndPreload({
      permalinkOrProductId: permalink || masterProductId,
      additionalQueryVariables: {
        withWarehouseInv: loadWarehouseInventory,
        useRetailPriceForDiscount,
        includeSwatchType: true,
        gridGender,
        membershipLevelGroup,
        filterOrphanedOptions: true,
        isWarehouseProduct: secondarySite === SecondarySites.OUTLET,
        relatedProductsImageData: true,
      },
    });

  useEffect(() => {
    if (product) {
      if (
        (secondarySite === SecondarySites.OUTLET &&
          !product?.isWarehouseProduct) ||
        (secondarySite !== SecondarySites.OUTLET && product?.isWarehouseProduct)
      ) {
        const prefix = secondarySite ? `/${secondarySite}` : '';
        router.replace(`${prefix}/${productDetailPath}/no-results`);
      }
    }
  }, [product, router, secondarySite]);

  let descriptionForHead;
  let titleForHead;
  let metadata;

  if (product) {
    metadata = product.metadata;
    const brandLabel = config.get('public.brand.label');

    if (metadata?.description) {
      descriptionForHead = '' + metadata.description;
    } else if (product?.description) {
      descriptionForHead = '' + product.description;
    } else {
      descriptionForHead = formatMessage(defaults.metaDescription, {
        productLabel: product?.label || '',
      });
    }

    if (metadata?.title) {
      titleForHead = metadata.title;
    } else {
      titleForHead = `${product?.label} ${formatMessage(
        defaults.metaTitleOfferDefault,
        { brand: brandLabel }
      )}`;
    }
  }

  return (
    <StyleContext>
      <CuralateUgcSnippet />
      <DefaultLayout description={descriptionForHead} title={titleForHead}>
        <PDPContent
          builderQueries={builderQueries}
          permalink={permalink}
          product={product}
          baseProductLoading={baseProductLoading}
          relatedProductsLoading={relatedProductsLoading}
          productError={productError}
        />
      </DefaultLayout>
    </StyleContext>
  );
}

ProductDetailPage.getInitialProps = async (ctx) => {
  const {
    query: { identifier },
  } = ctx;

  await ctx.store.dispatch(loadSession());
  const { isLoggedIn } = getSession(ctx.store.getState());

  try {
    const disposition = getDirectMarketingDisposition(ctx.store.getState());
    await getExperienceId({
      isLoggedIn,
      disposition,
      dmg: ctx.query?.dmg,
      gender: ctx.query?.gender,
      dispatch: ctx.store.dispatch,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('FAILED TO RETRIEVE DISPOSITION!!!!');
  }

  // Prefetch sessionDetails.
  // This helps with displaying the correct CTA on first client side render.
  if (isLoggedIn) {
    await ctx.store.dispatch(loadSessionDetails());
  }

  let masterProductId;
  let permalink;
  if (/^\d+$/.test(identifier)) {
    masterProductId = parseInt(identifier, 10);
  } else {
    permalink = getBrandValue({
      savage: transformIdentifier(identifier),
      default: identifier,
    });
  }
  const builderQueries = {
    isPreview: isBuilderPreview(ctx.req),
  };

  return {
    masterProductId,
    permalink,
    builderQueries,
  };
};

registerBuilderComponents(allComponents);
registerBuilderMenu();
ProductDetailPage.propTypes = {
  builderQueries: PropTypes.object,
  masterProductId: PropTypes.number,
  permalink: PropTypes.string,
};

export default createPage(ProductDetailPage, {
  prefetchFeatures: [
    featureNames.AFTERPAY,
    featureNames.RETAILPRICETEST,
    featureNames.BOUNCEBACK_ENDOWMENT,
    featureNames.SINGLE_CTA_VIP_OPT_IN,
    featureNames.ENABLE_PDP_VIDEO_DESCRIPTIONS,
    featureNames.KLARNA,
  ],
  resourceBundles: [
    'app_pdp',
    'app_retail_stores',
    'bops',
    'fit_guide',
    'grid',
    'pdp_change_model',
    'pdp_membership_select',
    'policy_promo',
    'scrubs_pdp',
    'scrubs_pdp_size',
    'site_memberlist',
    'site_pdp',
    'site_pdp_callout',
    'site_product_filter',
    'site_product_page',
    'site_product',
    'site_reviews',
    'site_sidebar_bops',
    'size_guide',
    'speedy_signup',
    'system_product_detail',
    'vip_opt_in',
  ],
});
